import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface PageLayoutContextType {
  // Header — title can be string or a ReactNode (e.g. editable document title)
  title: string | ReactNode | null;
  setTitle: (title: string | ReactNode | null) => void;
  actionsRef: React.RefObject<HTMLDivElement | null>;
  // Footer (e.g. dashboard composer)
  footerRef: React.RefObject<HTMLDivElement | null>;
  // Right column beside main content (e.g. document chat)
  contentSidebarRef: React.RefObject<HTMLDivElement | null>;
  /** Document route mounted DocumentChatFooter and wants the slot (follows `active`). */
  contentSidebarSlotActive: boolean;
  setContentSidebarSlotActive: (active: boolean) => void;
  /** User closed the panel while the slot is still active (desktop); hidden until reopened. */
  contentSidebarDismissed: boolean;
  setContentSidebarDismissed: (dismissed: boolean) => void;
  /** `slotActive && !dismissed` — whether the page content sidebar shell should render. */
  contentSidebarPanelVisible: boolean;
  /** Register handler for sidebar close (mobile: hide composer; desktop: dismiss). */
  registerContentSidebarCloseHandler: (handler: (() => void) | null) => void;
  requestContentSidebarClose: () => void;
}

const PageLayoutContext = createContext<PageLayoutContextType | undefined>(
  undefined,
);

export function PageLayoutProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | ReactNode | null>(null);
  const [contentSidebarSlotActive, setContentSidebarSlotActive] =
    useState(false);
  const [contentSidebarDismissed, setContentSidebarDismissed] = useState(true);
  const actionsRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contentSidebarRef = useRef<HTMLDivElement>(null);
  const contentSidebarCloseHandlerRef = useRef<(() => void) | null>(null);

  const registerContentSidebarCloseHandler = useCallback(
    (handler: (() => void) | null) => {
      contentSidebarCloseHandlerRef.current = handler;
    },
    [],
  );

  const requestContentSidebarClose = useCallback(() => {
    contentSidebarCloseHandlerRef.current?.();
  }, []);

  const contentSidebarPanelVisible =
    contentSidebarSlotActive && !contentSidebarDismissed;

  const value: PageLayoutContextType = {
    title,
    setTitle,
    actionsRef,
    footerRef,
    contentSidebarRef,
    contentSidebarSlotActive,
    setContentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
    contentSidebarPanelVisible,
    registerContentSidebarCloseHandler,
    requestContentSidebarClose,
  };

  return (
    <PageLayoutContext.Provider value={value}>
      {children}
    </PageLayoutContext.Provider>
  );
}

export function usePageLayout() {
  const context = useContext(PageLayoutContext);
  if (!context) {
    throw new Error('usePageLayout must be used within PageLayoutProvider');
  }

  return context;
}

export function usePageHeader() {
  const { title, setTitle, actionsRef } = usePageLayout();
  return { title, setTitle, actionsRef };
}

export function usePageFooter() {
  const { footerRef } = usePageLayout();
  return { footerRef };
}

export function usePageContentSidebar() {
  const {
    contentSidebarRef,
    contentSidebarSlotActive,
    setContentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
    contentSidebarPanelVisible,
    registerContentSidebarCloseHandler,
    requestContentSidebarClose,
  } = usePageLayout();
  return {
    contentSidebarRef,
    contentSidebarSlotActive,
    setContentSidebarSlotActive,
    contentSidebarDismissed,
    setContentSidebarDismissed,
    contentSidebarPanelVisible,
    registerContentSidebarCloseHandler,
    requestContentSidebarClose,
  };
}
